import apiClient from "@apis/index";
import { BannerResponse } from "@src/types/BannerAd";
import { HistoryRequest, StockItemRequest } from "@src/types/HistoryRequest";
import { SYSTEM_PLATFORM } from "@utils/index";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { HistoryUnreadRequest } from "./../types/HistoryUnreadRequest";
import Routes from "./routes";
import { useDispatch } from "react-redux";
import { setLastAdSequence, useUserInfo } from "@redux/slices/userInfoSlice";
import { RequestTypes } from "@scenes/requests/utils/utils";
import { PriceModes } from "@components/molecules/PriceModeCard/types";

const UNREAD_DATA_REFETCH_INTERVAL = 5 * 60 * 1000;

export enum WorkFlowActions {
  APPROVE = "approve",
  REQUEST_PRICE_CHANGE = "request_price_change",
  REQUEST_DECLINE = "request_decline",
}

export const useRequestHistoryData = (
  request_type: string,
  enabled: boolean = true,
  currency: any,
  priceMode: PriceModes
) =>
  useInfiniteQuery({
    cacheTime: 0,
    staleTime: 0,
    queryKey: ["stock_item_request", request_type, currency, priceMode],
    enabled: enabled,
    queryFn: ({ pageParam = 1 }) =>
      apiClient.get<HistoryRequest>(Routes.REQUEST_HISTORY, {
        params: {
          "stock_item_request[request_type]": request_type,
          "stock_item_request[page]": pageParam,
          price_mode: priceMode,
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const hasNextPage = (lastPage?.data?.data?.stock_item_requests || [])
        .length;
      return hasNextPage ? allPages.length + 1 : undefined;
    },
  });

export const useRequestHistoryDetail = (
  id: string,
  request_type?: RequestTypes,
  currency?: any,
  priceMode?: PriceModes
) =>
  useQuery({
    queryKey: ["stock_item_request_detail", id, currency, priceMode],
    cacheTime: 0,
    enabled: priceMode !== PriceModes.GREEN && !!id,
    queryFn: () =>
      apiClient.get<StockItemRequest>(
        request_type === RequestTypes.Order
          ? Routes.ORDER_REQUESTS(id)
          : Routes.STOCK_ITEM_REQUEST_DETAIL(id),
        {
          params: { price_mode: priceMode },
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        }
      ),
  });

export const useUpdateRequestHistory = (id: string) => {
  return useMutation({
    mutationFn: (data: any) => {
      return apiClient.put(Routes.STOCK_ITEM_REQUEST_DETAIL(id), data);
    },
  });
};

export const useUpdateOrderRequest = (id: string) => {
  return useMutation({
    mutationFn: (data: any) => {
      return apiClient.put(Routes.ORDER_REQUESTS(id), data);
    },
  });
};

export const useOrderActions = (id: string) => {
  return useMutation({
    mutationFn: (data: {
      status: "withdraw" | "accept" | "decline";
      ordered_item_ids: number[];
    }) => {
      return apiClient.post(Routes.ORDER_ACTIONS(id), data);
    },
  });
};

export const useWholeRequestOrderActions = (id: string) => {
  return useMutation({
    mutationFn: (data: {
      status: "withdraw" | "decline";
      action_comment?: string;
    }) => {
      return apiClient.post(Routes.ORDER_ACTIONS(id), data);
    },
  });
};

export const useUpdateBulkRequest = () => {
  return useMutation({
    mutationFn: (data: any) => {
      return apiClient.put(Routes.BULK_REQUEST_UPDATE, data);
    },
  });
};

export const useRequestHistoryDetailMessages = (id: string) =>
  useQuery({
    queryKey: ["stock_item_request_detail_messages", id],
    queryFn: () =>
      apiClient.get<StockItemRequest>(
        Routes.STOCK_ITEM_REQUEST_DETAIL_MESSAGES(id),
        {
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        }
      ),
  });

export const useSendRequestHistoryMessage = (id: string) => {
  return useMutation({
    mutationFn: (data: any) => {
      return apiClient.post(
        Routes.STOCK_ITEM_REQUEST_DETAIL_MESSAGES(id),
        data
      );
    },
  });
};

export const useAdBannerData = (
  productType: string,
  enabled: boolean,
  priceMode: number,
  activityType: string,
  platform?: string,
  mediaPurpose?: string
) => {
  const { lastAdSequesnce } = useUserInfo();
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["ad", activityType, productType, mediaPurpose],
    enabled,
    cacheTime: 0,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    keepPreviousData: false,
    queryFn: () =>
      apiClient.get<BannerResponse>(Routes.GET_AD_BANNER, {
        params: {
          product_type: productType,
          platform: platform,
          priceMode: priceMode,
          entity: "Organization",
          last_seen_ad_sequence: lastAdSequesnce,
          system_platform: SYSTEM_PLATFORM,
          media_purpose: mediaPurpose,
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "Cache-Control": "no-cache",
        },
      }),
    onSuccess: (data) => {
      if (data?.data?.response?.body?.ad?.sequence_number) {
        dispatch(
          setLastAdSequence(data?.data?.response?.body?.ad?.sequence_number)
        );
      }
    },
  });
};

export const useUnreadRequestCountData = (
  view: string,
  bmh_type: string,
  get_count: boolean,
  enabled = true,
  loadPagination?: boolean
) => {
  const queryKey = ["GET_UNREAD_REQUEST", view, bmh_type, get_count];

  const queryFn = ({ pageParam = 1 }) =>
    apiClient
      .get<HistoryUnreadRequest>(Routes.GET_UNREAD_REQUEST, {
        params: {
          "stock_item_request[view]": view,
          "stock_item_request[bmh_type]": bmh_type,
          ...(!get_count ? {} : { "stock_item_request[page]": pageParam }),
          "stock_item_request[get_count]": !get_count,
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      })
      .then((response) => response.data);

  if (get_count && loadPagination === false) {
    return useInfiniteQuery({
      cacheTime: 0,
      staleTime: 0,
      enabled,
      refetchInterval: UNREAD_DATA_REFETCH_INTERVAL,
      queryKey,
      queryFn: () => queryFn({ pageParam: 1 }),
    });
  } else {
    return useInfiniteQuery({
      cacheTime: 0,
      staleTime: 0,
      enabled,
      refetchInterval: UNREAD_DATA_REFETCH_INTERVAL,
      queryKey,
      queryFn,
      getNextPageParam: (lastPage, allPages) => {
        const hasNextPage = (lastPage?.data?.stock_item_request || []).length;
        return hasNextPage ? allPages.length + 1 : undefined;
      },
    });
  }
};

export const useUpdateReadRequestStatus = (id: number) => {
  return useMutation({
    mutationFn: (data: any) => {
      return apiClient.patch(Routes.UPDATE_READ_REQUEST(id), data);
    },
  });
};

export const useRequestChildrenAPI = (id: number) => {
  return useMutation({
    mutationFn: (data: any) => {
      return apiClient.get(Routes.GET_REQUEST_CHILDREN(id), { params: data });
    },
  });
};
